<template>
    <div
        :class="{ active: isActive, 'panel--vertical-tabs': verticalTabs }"
        class="panel is-flex-grow-3"
        ref="panelRef"
    >
        <mention-access-denied
            v-if="accessDenied && !loading"
            :subject-type="entityType"
            :subject-id="entityId"
            :comment-id="route.query.comment"
            @done="leaveDeniedRecord"
        />
        <app-notfound v-else-if="!data && !loading" />

        <b-loading :model-value="loading" :is-full-page="false"></b-loading>

        <template v-if="data">
            <!-- Header -->
            <div class="preview-header-wrapper">
                <div class="preview-header action-bar">
                    <!-- A record is reached from more than its own list — a related
                         document, a search result, a dashboard link — so this walks
                         the history; the list is only the fallback. -->
                    <p class="control">
                        <back-button
                            :to="{ name: listRouteName, query: listQuery }"
                            :entry="entry"
                            class="button is-small"
                            :title="$t('Back')"
                        />
                    </p>
                    <div class="preview-header__title">
                        <slot name="title" :data="data">
                            <h3 class="title is-4 m-0 has-ellipsis" :title="data[labelField]">
                                {{ data[labelField] }}
                            </h3>
                        </slot>
                        <div v-if="$slots['after-title']" class="preview-header__after-title">
                            <slot name="after-title" :data="data"></slot>
                        </div>
                    </div>
                    <div id="preview-header-actions" class="preview-header__actions field is-grouped">
                        <slot name="actions" :data="data">
                            <p class="control">
                                <a @click="onRemove" :title="$t('Remove')" class="button is-small">
                                    <b-tooltip :label="$t('Remove')" :delay="500" append-to-body>
                                        <b-icon icon="delete" size="is-small"></b-icon>
                                    </b-tooltip>
                                </a>
                            </p>
                        </slot>

                        <!-- Close selection: back to the full-width list -->
                        <p class="control preview-header__close is-hidden-touch">
                            <router-link
                                :to="{ name: listRouteName, query: listQuery }"
                                class="button is-small"
                                replace
                                :title="$t('Close')"
                            >
                                <b-tooltip :label="$t('Close')" :delay="500" append-to-body>
                                    <b-icon icon="close" size="is-small"></b-icon>
                                </b-tooltip>
                            </router-link>
                        </p>
                    </div>
                </div>

                <!-- Toolbar — full width under the header when the tabs run
                     horizontally. With a tab rail it moves into the content
                     column instead, so the rail starts beside it. -->
                <div v-if="$slots.toolbar && !usesTabRail" class="preview-toolbar">
                    <slot name="toolbar" :data="data"></slot>
                </div>

                <!-- Horizontal Tabs -->
                <div v-if="resolvedTabs.length > 0 && !verticalTabs" class="tabs">
                    <ul>
                        <slot name="tabs" :data="data" :primaryTabs="primaryTabs" :secondaryTabs="secondaryTabs">
                            <router-link
                                v-for="tab in primaryTabs"
                                :key="tab.tabKey || tab.name"
                                :to="getTabRoute(tab)"
                                custom
                                v-slot="{ navigate, isExactActive, isActive: tabIsActive }"
                            >
                                <li :class="{ 'is-active': tab.exact ? isExactActive : tabIsActive }">
                                    <a @click="selectTab(tab, navigate)">{{ $t(tab.label) }}</a>
                                </li>
                            </router-link>

                            <!-- Options dropdown for secondary tabs -->
                            <li v-if="secondaryTabs.length > 0">
                                <b-dropdown position="is-bottom-left" append-to-body>
                                    <template #trigger>
                                        <a>
                                            <span>{{ $t('More') }}</span>
                                            <b-icon size="is-small" icon="chevron-down"></b-icon>
                                        </a>
                                    </template>
                                    <template
                                        v-for="(tab, index) in secondaryTabs"
                                        :key="tab.tabKey || tab.name || 'sep-' + index"
                                    >
                                        <b-dropdown-item v-if="tab.separator" separator />
                                        <b-dropdown-item v-else has-link>
                                            <router-link
                                                :to="getTabRoute(tab)"
                                                class="dropdown-item"
                                                active-class="is-active"
                                            >
                                                {{ $t(tab.label) }}
                                            </router-link>
                                        </b-dropdown-item>
                                    </template>
                                </b-dropdown>
                            </li>
                        </slot>
                    </ul>
                </div>
            </div>

            <!-- Content + Vertical Tabs (right sidebar) layout -->
            <div v-if="usesTabRail" class="vertical-tabs-layout">
                <div
                    class="vertical-tabs-content tabs-details"
                    :class="{ 'vertical-tabs-content--pinned': tabsPinned }"
                >
                    <div v-if="$slots.toolbar" class="preview-toolbar">
                        <slot name="toolbar" :data="data"></slot>
                    </div>

                    <div class="vertical-tabs-scroll">
                        <slot name="content" :data="data">
                            <router-view v-slot="{ Component }">
                                <transition name="tab-fade" mode="out-in">
                                    <component
                                        v-if="Component"
                                        :is="Component"
                                        :data="data"
                                        :id="entityId"
                                        @updated="onChildUpdated"
                                        @reload="onReload"
                                    />
                                </transition>
                            </router-view>
                        </slot>
                    </div>

                    <!-- Pinned composer/quick-add bar: sits at the bottom of the content
                         column, to the left of the tab nav (desktop and mobile). -->
                    <div v-if="$slots['bottom-bar']" class="vertical-tabs-footer">
                        <slot name="bottom-bar" :data="data" />
                    </div>
                </div>

                <aside
                    class="vertical-tabs-nav"
                    :class="{ 'vertical-tabs-nav--pinned': tabsPinned, 'vertical-tabs-nav--hovered': navHovered }"
                    @mouseenter="navHovered = true"
                    @mouseleave="navHovered = false"
                >
                    <ul>
                        <slot name="tabs" :data="data" :primaryTabs="primaryTabs" :secondaryTabs="secondaryTabs">
                            <template v-for="tab in resolvedTabs" :key="tab.tabKey || tab.name || 'sep-' + tab.label">
                                <li v-if="tab.separator" class="vertical-tabs-separator">
                                    <span v-if="tab.label">{{ $t(tab.label) }}</span>
                                </li>
                                <router-link
                                    v-else-if="tab.name"
                                    :to="getTabRoute(tab)"
                                    custom
                                    v-slot="{ navigate, isExactActive, isActive: tabIsActive }"
                                >
                                    <li
                                        :class="[
                                            { 'is-active': tab.exact ? isExactActive : tabIsActive },
                                            { 'vertical-tabs-inactive': tab.inactive },
                                            { 'vertical-tabs-sub': tab.sub }
                                        ]"
                                    >
                                        <a @click="selectTab(tab, navigate)">
                                            <b-icon v-if="tab.icon" :icon="tab.icon" size="is-small"></b-icon>
                                            <span class="vertical-tabs-label">{{ $t(tab.label) }}</span>
                                            <!-- How much a tab holds, so the rail
                                                 says where the work is before it
                                                 is opened. Absent rather than a
                                                 zero: an empty tab has nothing to
                                                 announce, and a column of noughts
                                                 is what makes the counts that
                                                 matter hard to see. -->
                                            <span v-if="tab.count" class="vertical-tabs-count">{{ tab.count }}</span>
                                            <b-icon
                                                v-if="tab.inactive"
                                                icon="plus-circle-outline"
                                                size="is-small"
                                                class="ml-auto"
                                            ></b-icon>
                                        </a>
                                    </li>
                                </router-link>
                                <li v-else-if="tab.inactive" class="vertical-tabs-inactive">
                                    <a @click="tab.onClick && tab.onClick()">
                                        <b-icon v-if="tab.icon" :icon="tab.icon" size="is-small"></b-icon>
                                        <span class="vertical-tabs-label">{{ $t(tab.label) }}</span>
                                        <b-icon icon="plus-circle-outline" size="is-small" class="ml-auto"></b-icon>
                                    </a>
                                </li>
                            </template>
                        </slot>
                    </ul>
                    <div class="vertical-tabs-pin">
                        <a @click="toggleTabsPin" :title="tabsPinned ? $t('Unpin sidebar') : $t('Pin sidebar')">
                            <b-icon :icon="tabsPinned ? 'pin-off' : 'pin'" size="is-small"></b-icon>
                        </a>
                    </div>
                </aside>

                <!-- Mobile bottom tabs -->
                <nav class="vertical-tabs-bottom">
                    <slot name="tabs" :data="data" :primaryTabs="primaryTabs" :secondaryTabs="secondaryTabs">
                        <router-link
                            v-for="tab in resolvedTabs.filter(t => !t.separator && t.name)"
                            :key="tab.tabKey || tab.name"
                            :to="getTabRoute(tab)"
                            custom
                            v-slot="{ navigate, isExactActive, isActive: tabIsActive }"
                        >
                            <a
                                @click="selectTab(tab, navigate)"
                                :class="{ 'is-active': tab.exact ? isExactActive : tabIsActive }"
                                class="vertical-tabs-bottom__item"
                            >
                                <b-icon v-if="tab.icon" :icon="tab.icon" size="is-small"></b-icon>
                                <span>{{ $t(tab.label) }}</span>
                            </a>
                        </router-link>
                    </slot>
                </nav>
            </div>

            <!-- Horizontal Tab content -->
            <div v-if="!verticalTabs" class="tabs-details" style="position: relative">
                <slot name="content" :data="data">
                    <router-view v-slot="{ Component }">
                        <transition name="tab-fade" mode="out-in">
                            <component
                                v-if="Component"
                                :is="Component"
                                :data="data"
                                :id="entityId"
                                @updated="onChildUpdated"
                                @reload="onReload"
                            />
                        </transition>
                    </router-view>
                </slot>
            </div>
        </template>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, inject, provide } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useEntityDetail } from '@/composables/useEntityDetail';
import { useOrgStore } from '@/stores/org';
import { useAppStore } from '@/stores/app';
import { useUserStore } from '@/stores/user';
import { debounce } from 'lodash';
import MentionAccessDenied from '@/components/MentionAccessDenied.vue';

const props = defineProps({
    // Entity ID (from route param)
    id: {
        type: [String, Number],
        required: true
    },
    // API resource name
    resource: {
        type: String,
        required: true
    },
    // Route name for list (back button)
    listRouteName: {
        type: String,
        required: true
    },
    // Field to display as title
    labelField: {
        type: String,
        default: 'display_name'
    },
    // Page title prefix
    titlePrefix: {
        type: String,
        default: ''
    },
    // Tab configuration (static)
    // Each tab: { name: 'route-name', label: 'Tab Label', exact: false, primary: true }
    // Separators in secondary: { separator: true, primary: false }
    tabs: {
        type: Array,
        default: () => []
    },
    // Dynamic tabs: function that receives data and returns tabs array
    tabsFn: {
        type: Function,
        default: null
    },
    // Entity type for polymorphic components (comments, activities, logs, etc.)
    entityType: {
        type: String,
        default: ''
    },
    // Display tabs vertically on the left side instead of horizontally
    verticalTabs: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['loaded', 'removed', 'updated']);

const route = useRoute();
const router = useRouter();
const orgStore = useOrgStore();

// Back-navigation markers (e.g. from the dashboard "My tasks" widget) belong to
// a single preview; strip them so returning to the list keeps the URL clean and
// they don't get re-injected into the next preview opened from that list.
const listQuery = computed(() => {
    const { from_name: _fn, from_id: _fi, ...query } = sharedQuery.value;
    return query;
});

/**
 * The query every tab carries — the list's own filters and sort, so the list
 * beside the record keeps them. A tab may also read keys of its own (the
 * reports feed narrowed to one visit); it declares them in its route's
 * `meta.tabQuery`, and they stay behind when another tab or the list is opened.
 */
const sharedQuery = computed(() => {
    const own = route.meta?.tabQuery ?? [];
    return Object.fromEntries(Object.entries(route.query).filter(([key]) => !own.includes(key)));
});
const appStore = useAppStore();
const userStore = useUserStore();

// Inject parent functions
const parentEdit = inject('parentEdit', null);
const parentRemove = inject('parentRemove', null);
const parentUpdateRow = inject('parentUpdateRow', null);

// Provide context to child route components (shared tab components)
provide(
    'previewEntityType',
    computed(() => props.entityType)
);
provide(
    'previewListRouteName',
    computed(() => props.listRouteName)
);
provide('parentRefresh', () => loadData());

// Vertical tabs pin state (persisted in localStorage)
const tabsPinned = ref(localStorage.getItem('vertical-tabs-pinned') === 'true');

// JS-managed hover state — avoids CSS :hover being lost after Vue re-renders on route change
const navHovered = ref(false);

// Set when a forbidden record is opened from a mention notification (?comment=
// for a comment mention, ?mention=1 for a description one): we show an
// access-denied panel with a "request access" action instead of silently
// bouncing the user to an (often empty) list.
const accessDenied = ref(false);

const arrivedFromMention = computed(() => !!route.query.comment || !!route.query.mention);

function leaveDeniedRecord() {
    accessDenied.value = false;
    router.push({ name: 'dashboard', params: { org: route.params.org } });
}

function toggleTabsPin() {
    tabsPinned.value = !tabsPinned.value;
    localStorage.setItem('vertical-tabs-pinned', tabsPinned.value);
}

// Track ID changes from route
const usedId = ref(0);

const entityId = computed(() => usedId.value || props.id);

// Build API URL (org-scoped when inside an organization, global otherwise)
const apiBase = computed(() => (route.params.org ? orgStore.api : '/api'));
const apiUrl = computed(() => `${apiBase.value}/${props.resource}/${entityId.value}`);

// Use entity detail composable only for its reactive `data`/`loading` refs;
// fetching is handled manually by loadData below, which builds the correct
// `/{id}` URL and keeps the parent list row in sync. The composable's own
// load/refresh are intentionally not used here.
const { data, loading } = useEntityDetail(
    () => `${apiBase.value}/${props.resource}`,
    () => entityId.value,
    { immediate: false, debounceMs: 300 }
);

/**
 * Keep the master list's row in step with the record on screen.
 *
 * Watched rather than called from each path that changes the record: a preview
 * refreshing a status, cancelling a transaction or applying an action's
 * response assigns `preview.value.data` straight from the outside (two dozen
 * places across the modules), which no amount of remembering inside `loadData`
 * would ever cover. The row follows the record, whoever replaced it.
 *
 * Deep, because half of those paths *mutate* the record rather than replace it
 * — `Object.assign(data, response.data)` after a save, an approval, a status
 * change. A shallow watch never fires for those, which is why a status edited
 * on the fiche kept showing its old value in the list until a reload.
 */
watch(
    data,
    record => {
        if (parentUpdateRow && record?.id) {
            parentUpdateRow(record.id, record);
        }
    },
    { deep: true }
);

// Check if preview is active
const isActive = computed(() => props.id > 0);

// Resolve tabs: use tabsFn if provided, otherwise static tabs
const resolvedTabs = computed(() => {
    if (props.tabsFn && data.value) {
        return props.tabsFn(data.value);
    }
    return props.tabs;
});

// Whether the tabs render as the right-hand rail. Also decides where the
// toolbar goes: beside the rail rather than above it, so the rail runs the full
// height of the record instead of starting halfway down.
const usesTabRail = computed(() => props.verticalTabs && resolvedTabs.value.length > 0);

// Split tabs into primary and secondary
const primaryTabs = computed(() => resolvedTabs.value.filter(t => t.primary !== false));
const secondaryTabs = computed(() => resolvedTabs.value.filter(t => t.primary === false));

// A record opens on its overview — every time, for everyone. No tab is
// remembered across records: the rail is where the user looks for the tab they
// want, and reopening one they had left behind on another record is a surprise,
// not a shortcut. A deep link and a back/forward still land on the tab they
// name; only the *landing* on a record is fixed.
function selectTab(tab, navigate) {
    navigate();
    if (tab.onClick) {
        tab.onClick();
    }
}

/**
 * Where this record was entered, in the browser's history — so the back arrow
 * leaves the record for the screen it was opened from (the list, a search
 * result, another module), rather than stepping back through the tabs visited
 * since. Read once, when the preview is created: it stays mounted across tab
 * changes and across records picked from the list beside it, so the entry
 * stays the one from before the record. See resolveLeave().
 */
const entry = {
    position: window.history.state?.position,
    hadBack: window.history.state?.back !== null && window.history.state?.back !== undefined
};

// Load on mount
onMounted(() => {
    fetchData();
    subscribeToCollaboratorAdded();
});

// When the viewer is parked on the access-denied panel (arrived via a mention
// they cannot yet see) and someone adds them as a collaborator elsewhere, the
// backend now grants access — but this page has no reason to refetch on its own.
// A per-user "CollaboratorAdded" cue lets us re-run loadData() and reveal the
// record without a manual reload. Best-effort: if Echo is down, a reload still
// works. The user channel is shared with the notification bell, so we detach
// only our own listener on unmount (never Echo.leave()).
let userChannelName = null;

function onCollaboratorAdded(payload) {
    if (!accessDenied.value) return;
    if (payload?.org_slug !== orgStore.slug) return;
    if (payload?.type !== props.entityType) return;
    if (String(payload?.id) !== String(entityId.value)) return;

    loadData();
}

async function subscribeToCollaboratorAdded() {
    if (!userStore.id) return;
    try {
        const Echo = await window.initEcho();
        userChannelName = `App.Models.User.${userStore.id}`;
        Echo.private(userChannelName).listen('.CollaboratorAdded', onCollaboratorAdded);
    } catch {
        // best-effort — a manual reload still reveals the record once access is granted
    }
}

onBeforeUnmount(() => {
    if (userChannelName && window.Echo) {
        window.Echo.private(userChannelName).stopListening('.CollaboratorAdded', onCollaboratorAdded);
    }
});

// Watch for route param changes
watch(
    () => route.params.id,
    newId => {
        if (newId) {
            usedId.value = newId;
            loadData();
        }
    }
);

// Refetch when something changed this record from outside its page (e.g. a
// collaborator added via the notification bell).
watch(
    () => appStore.recordRefresh,
    signal => {
        if (signal && signal.type === props.entityType && String(entityId.value) === signal.id) {
            loadData();
        }
    }
);

// Each fetch takes a number; only the latest may write. The first load no longer
// waits for the debounce, so a switch landing right after it could otherwise
// be overwritten by the slower, older answer.
let latestRequest = 0;

async function fetchData() {
    if (!entityId.value) return;

    const request = ++latestRequest;
    loading.value = true;
    accessDenied.value = false;
    try {
        const response = await axios.get(apiUrl.value);
        if (request !== latestRequest) return;
        data.value = response.data;
        emit('loaded', data.value);

        // Update page title
        if (props.titlePrefix && data.value[props.labelField]) {
            document.title = `${data.value[props.labelField]} > ${i18n.t(props.titlePrefix)} | ${AppTitle}`;
        }
    } catch (err) {
        if (request !== latestRequest) return;
        const status = err.response?.status;
        // Arrived from a mention notification on a record we cannot see: show the
        // access-denied panel instead of the silent list redirect.
        if (status === 403 && arrivedFromMention.value) {
            accessDenied.value = true;
        } else if ((status === 403 || status === 404) && props.listRouteName) {
            router.replace({ name: props.listRouteName, query: route.query });
        } else {
            new AxiosError(err.response).manage();
        }
    } finally {
        if (request === latestRequest) {
            loading.value = false;
        }
    }
}

// Collapses a burst — arrowing through the master-detail list, refresh signals —
// into one request. Never on the first load: nothing precedes it to collapse,
// and the 300 ms were paid on every record opened.
const loadData = debounce(fetchData, 300);

function getTabRoute(tab) {
    const params = { id: entityId.value, ...tab.params };

    // Handle dynamic params from data
    if (tab.dataParams && data.value) {
        for (const [key, field] of Object.entries(tab.dataParams)) {
            params[key] = data.value[field];
        }
    }

    return {
        name: tab.name,
        params,
        // A tab opens on its whole content: a key the current tab reads for
        // itself never rides along, even back to that same tab.
        query: sharedQuery.value
    };
}

/**
 * Pages that need their own wording pass it here — `onRemove({ confirmTitle,
 * confirmMessage, successMessage })`. The built-in trash button above binds
 * `@click="onRemove"`, so the first argument may well be a MouseEvent: anything
 * that is not a plain object is not options.
 */
function onRemove(options) {
    const opts = options && typeof options === 'object' && !(options instanceof Event) ? options : {};

    if (parentRemove && data.value) {
        parentRemove(data.value, opts);
    }
}

function onEdit() {
    if (parentEdit && data.value) {
        parentEdit(data.value);
    }
}

// Handle events from child route components
function onChildUpdated(updatedData) {
    if (updatedData) {
        data.value = { ...data.value, ...updatedData };
    }
    emit('updated', updatedData);
}

function onReload() {
    loadData();
}

// Expose for parent components
defineExpose({
    data,
    loading,
    loadData,
    // `refresh` re-fetches the detail (correct `/{id}` URL) and syncs the list
    // row — used by previews after publish/retire and other mutating actions.
    refresh: loadData,
    onEdit,
    onRemove
});
</script>

<style scoped>
/* Wraps rather than overlaps: once the title cannot keep its minimum beside the
   actions (a phone, a narrow split panel), the actions drop to a row of their
   own instead of running over the number and the status tags. Decided by the
   content, not a breakpoint — each module's actions are a different width. */
.preview-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    /* Query container so the after-title stats hide based on the header's own
       width (a resizable split panel), not the viewport — see below. */
    container-type: inline-size;
    container-name: preview-header;
}

.preview-header__title {
    flex: 1 1 12rem;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

/* Modules that override the #title slot wrap the heading in their own flex row
   (back button, avatar, badges…). A flex item defaults to min-width: auto, so
   that wrapper — and the heading inside it — refuses to shrink and a long title
   runs under the action buttons instead of ellipsing. */
.preview-header__title > * {
    min-width: 0;
}

.preview-header__title :deep(.title) {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Icon buttons / avatars sitting beside the title keep their size; only the
   heading gives way. */
.preview-header__title :deep(.button),
.preview-header__title :deep(.tag) {
    flex-shrink: 0;
}

.preview-header__after-title {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
}

.preview-header__actions {
    flex-shrink: 0;
    flex-wrap: wrap;
    row-gap: 0.4rem;
    max-width: 100%;
    margin-left: auto;
}

/* When the header is too narrow to fit the title comfortably beside them, drop
   the after-title stats (communication shortcuts) rather than crushing the
   title — the same counts remain reachable from their tabs. */
@container preview-header (max-width: 520px) {
    .preview-header__after-title {
        display: none;
    }
}

@media screen and (max-width: 768px) {
    .preview-header__title :deep(.title) {
        font-size: 1.1rem !important;
    }
}

/* Toolbar — sits between header and tabs/content */
.preview-toolbar {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.4rem 0.5rem;
    border-top: 1px solid var(--bulma-border);
    background: var(--bulma-scheme-main-bis);
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
}

.preview-toolbar :deep(.button) {
    border: none;
    background: transparent;
    color: var(--bulma-text-weak);
    box-shadow: none;
    border-radius: var(--bulma-radius);
    flex-shrink: 0;
    white-space: nowrap;
    transition:
        background-color 0.15s ease,
        color 0.15s ease;
}

.preview-toolbar :deep(.button:hover) {
    background-color: hsl(var(--bulma-primary-h) var(--bulma-primary-s) var(--bulma-primary-l) / 0.08);
    color: var(--bulma-primary);
}

.preview-toolbar :deep(.preview-toolbar-separator) {
    width: 1px;
    height: 1rem;
    flex-shrink: 0;
    background: var(--bulma-border);
    margin: 0 0.15rem;
}

.preview-toolbar :deep(.preview-toolbar-spacer) {
    flex: 1;
    min-width: 0.25rem;
}

/* Panel is always a flex column so the header stays fixed and content scrolls */
.panel {
    display: flex;
    flex-direction: column;
    overflow: hidden !important;
}

/* Horizontal tab content fills remaining space and scrolls independently */
.tabs-details {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.preview-header-wrapper {
    flex-shrink: 0;
    border-bottom: 1px solid var(--bulma-border);
}

/* Horizontal tabs scroll horizontally when they overflow on narrow screens */
.tabs {
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
}

.tabs::-webkit-scrollbar {
    display: none;
}

/* Vertical tabs layout — fills all remaining height */
.vertical-tabs-layout {
    display: flex;
    flex: 1;
    min-height: 0;
}

.vertical-tabs-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
}

/* Scrollable tab body — sits above the pinned footer/composer bar. */
.vertical-tabs-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: auto;
    position: relative;
}

/* Pinned composer/quick-add bar at the bottom of the content column. */
.vertical-tabs-footer {
    flex-shrink: 0;
}

/* Sidebar nav — in-flow flex item, collapsed (icons only), expands on hover */
.vertical-tabs-nav {
    width: 40px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border-left: 1px solid var(--bulma-border);
    background: var(--bulma-scheme-main);
    overflow: hidden;
    z-index: 10;
    transition:
        width 0.2s ease,
        box-shadow 0.2s ease;
}

.vertical-tabs-nav--hovered {
    width: 210px;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.08);
    overflow-y: auto;
}

/* Pinned state — stays expanded */
.vertical-tabs-nav--pinned {
    width: 210px;
    overflow-y: auto;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
}

/* Pin button — always visible at the bottom of the sidebar */
.vertical-tabs-pin {
    display: flex;
    justify-content: center;
    padding: 0.35rem 0.5rem;
    margin-top: auto;
    border-top: 1px solid var(--bulma-border);
}

.vertical-tabs-pin a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--bulma-radius);
    color: var(--bulma-text-weak);
    cursor: pointer;
    transition:
        background-color 0.15s,
        color 0.15s;
}

.vertical-tabs-pin a:hover {
    background-color: hsl(var(--bulma-primary-h) var(--bulma-primary-s) var(--bulma-primary-l) / 0.08);
    color: var(--bulma-primary);
}

.vertical-tabs-nav ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.vertical-tabs-nav li {
    margin: 1px 0;
    white-space: nowrap;
}

.vertical-tabs-nav li a {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.55rem 0.85rem;
    color: var(--bulma-text);
    font-size: 0.9rem;
    border-right: 3px solid transparent;
    transition:
        background-color 0.15s,
        border-color 0.15s,
        color 0.15s;
}

.vertical-tabs-nav li a:hover {
    background-color: hsl(var(--bulma-primary-h) var(--bulma-primary-s) var(--bulma-primary-l) / 0.06);
    color: var(--bulma-text-strong);
}

.vertical-tabs-nav li.is-active a {
    color: var(--bulma-primary-bold);
    border-right-color: var(--bulma-primary);
    background-color: hsl(var(--bulma-primary-h) var(--bulma-primary-s) var(--bulma-primary-l) / 0.1);
    font-weight: 600;
}

/* Hide labels & extras when collapsed, show on hover or pinned */
.vertical-tabs-nav :deep(.vertical-tabs-label),
.vertical-tabs-nav :deep(.vertical-tabs-count),
.vertical-tabs-nav :deep(.ml-auto) {
    opacity: 0;
    transition: opacity 0.15s ease;
}

.vertical-tabs-nav--hovered :deep(.vertical-tabs-label),
.vertical-tabs-nav--hovered :deep(.vertical-tabs-count),
.vertical-tabs-nav--hovered :deep(.ml-auto),
.vertical-tabs-nav--pinned :deep(.vertical-tabs-label),
.vertical-tabs-nav--pinned :deep(.vertical-tabs-count),
.vertical-tabs-nav--pinned :deep(.ml-auto) {
    opacity: 1;
}

/* Right-aligned so the numbers line up down the rail and can be compared at a
   glance; it fades with the labels, because a bare number in a 48px strip
   names nothing. */
.vertical-tabs-count {
    margin-left: auto;
    font-size: 0.7rem;
    line-height: 1;
    padding: 0.15rem 0.35rem;
    border-radius: 9px;
    color: var(--bulma-text-weak);
    background: color-mix(in srgb, var(--bulma-text) 10%, transparent);
}

.vertical-tabs li.is-active .vertical-tabs-count {
    color: var(--bulma-link-text);
    background: color-mix(in srgb, var(--bulma-link) 18%, transparent);
}

.vertical-tabs-nav :deep(.ml-auto) {
    margin-left: auto;
}

/* Separator heading */
.vertical-tabs-nav :deep(.vertical-tabs-separator) {
    margin: 0;
    padding: 0.6rem 0.85rem 0.25rem;
    overflow: hidden;
    height: 0;
    padding: 0;
    transition:
        height 0.2s ease,
        padding 0.2s ease;
}

.vertical-tabs-nav--hovered :deep(.vertical-tabs-separator),
.vertical-tabs-nav--pinned :deep(.vertical-tabs-separator) {
    height: auto;
    padding: 0.6rem 0.85rem 0.25rem;
}

.vertical-tabs-nav :deep(.vertical-tabs-separator) span {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--bulma-text-weak);
}

/* Sub-items (indented children) — only indent when expanded */
.vertical-tabs-nav :deep(.vertical-tabs-sub) a {
    font-size: 0.85rem;
}

.vertical-tabs-nav--hovered :deep(.vertical-tabs-sub) a,
.vertical-tabs-nav--pinned :deep(.vertical-tabs-sub) a {
    padding-left: 2.25rem;
}

/* Inactive / not-yet-activated roles */
.vertical-tabs-nav :deep(.vertical-tabs-inactive) a {
    color: var(--bulma-text-weak);
}

.vertical-tabs-nav :deep(.vertical-tabs-inactive) a:hover {
    color: var(--bulma-text);
    background-color: var(--bulma-background);
}

/* Mobile bottom tabs (horizontal) */
.vertical-tabs-bottom {
    display: none;
}

@media screen and (max-width: 768px) {
    .vertical-tabs-nav {
        display: none !important;
    }

    .vertical-tabs-layout {
        flex-direction: column;
        overflow: hidden;
    }

    .vertical-tabs-content {
        flex: 1;
        min-height: 0;
    }

    .vertical-tabs-bottom {
        display: flex !important;
        overflow-x: auto;
        scrollbar-width: none;
        -webkit-overflow-scrolling: touch;
        border-top: 1px solid var(--bulma-border);
        background: var(--bulma-scheme-main);
        flex-shrink: 0;
    }

    .vertical-tabs-bottom__item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.15rem;
        padding: 0.5rem 0.75rem;
        font-size: 0.7rem;
        color: var(--bulma-text-weak);
        white-space: nowrap;
        flex-shrink: 0;
        border-top: 2px solid transparent;
        transition:
            color 0.15s,
            border-color 0.15s;
    }

    .vertical-tabs-bottom__item.is-active {
        color: var(--bulma-primary-bold);
        border-top-color: var(--bulma-primary);
        font-weight: 600;
    }

    .vertical-tabs-bottom__item:hover {
        color: var(--bulma-text-strong);
    }
}
</style>
